import type { Component } from 'vue'
import ApplicationWindowIcon from '~/../member-webapp-shared/components/icons/streamline/ApplicationWindowIcon.vue'
import InvestmentAgreementIcon from '~/../member-webapp-shared/components/icons/streamline/InvestmentAgreementIcon.vue'
import MoneySharingGiveIcon from '~/../member-webapp-shared/components/icons/streamline/MoneySharingGiveIcon.vue'
import ShopIcon from '~/../member-webapp-shared/components/icons/streamline/ShopIcon.vue'
import UserSideIcon from '~/../member-webapp-shared/components/icons/streamline/UserSideIcon.vue'
import {
  GlobalSearchRecordTypeEnum,
  type GlobalSearchHit,
} from '~~/models/graphql/generated/types'

const GLOBAL_SEARCH_HREFS: Record<
  GlobalSearchRecordTypeEnum,
  (recordId: string) => string
> = {
  [GlobalSearchRecordTypeEnum.AmendedPledge]: (id) => `/amended-pledges/${id}`,
  [GlobalSearchRecordTypeEnum.CaseRecord]: (id) => `/cases/${id}`,
  [GlobalSearchRecordTypeEnum.Charity]: (id) => `/charities/${id}`,
  [GlobalSearchRecordTypeEnum.CharityBankAccount]: () => '/charities',
  [GlobalSearchRecordTypeEnum.CharityCategory]: (id) =>
    `/charity-categories/${id}`,
  [GlobalSearchRecordTypeEnum.Company]: (id) => `/companies/${id}`,
  [GlobalSearchRecordTypeEnum.Contact]: (id) => `/contacts/${id}`,
  [GlobalSearchRecordTypeEnum.Contribution]: (id) => `/cases/${id}`,
  [GlobalSearchRecordTypeEnum.Employee]: (id) => `/employees/${id}`,
  [GlobalSearchRecordTypeEnum.Event]: (id) => `/events/${id}`,
  [GlobalSearchRecordTypeEnum.Fees]: (id) => `/cases/${id}`,
  [GlobalSearchRecordTypeEnum.Fund]: (id) => `/funds/${id}`,
  [GlobalSearchRecordTypeEnum.Grant]: (id) => `/grants/${id}`,
  [GlobalSearchRecordTypeEnum.Hifo]: (id) => `/hifo-management/${id}`,
  [GlobalSearchRecordTypeEnum.HifoCascade]: (id) => `/hifo-cascades/${id}`,
  [GlobalSearchRecordTypeEnum.MatchCampaign]: (id) => `/match-campaigns/${id}`,
  [GlobalSearchRecordTypeEnum.MatchCampaignCategory]: (id) =>
    `/match-campaign-categories/${id}`,
  [GlobalSearchRecordTypeEnum.NewsItem]: (id) => `/news/${id}`,
  [GlobalSearchRecordTypeEnum.Policy]: (id) => `/training-admin/policies/${id}`,
  [GlobalSearchRecordTypeEnum.ProvisionalPledge]: (id) => `/pledges/${id}`,
  [GlobalSearchRecordTypeEnum.Role]: (id) => `/roles/${id}`,
  [GlobalSearchRecordTypeEnum.TargetAudience]: (id) =>
    `/target-audiences/${id}`,
  [GlobalSearchRecordTypeEnum.TrainingModule]: (id) =>
    `/training-admin/modules/${id}`,
  [GlobalSearchRecordTypeEnum.User]: (id) => `/users/${id}`,
}

const GLOBAL_SEARCH_RECORD_TYPE_LABELS: Record<
  GlobalSearchRecordTypeEnum,
  string
> = {
  [GlobalSearchRecordTypeEnum.AmendedPledge]: 'Amended Pledge',
  [GlobalSearchRecordTypeEnum.CaseRecord]: 'Case',
  [GlobalSearchRecordTypeEnum.Charity]: 'Charity',
  [GlobalSearchRecordTypeEnum.CharityBankAccount]: 'Bank Account',
  [GlobalSearchRecordTypeEnum.CharityCategory]: 'Charity Category',
  [GlobalSearchRecordTypeEnum.Company]: 'Company',
  [GlobalSearchRecordTypeEnum.Contact]: 'Contact',
  [GlobalSearchRecordTypeEnum.Contribution]: 'Contribution',
  [GlobalSearchRecordTypeEnum.Employee]: 'Employee',
  [GlobalSearchRecordTypeEnum.Event]: 'Event',
  [GlobalSearchRecordTypeEnum.Fees]: 'Fees',
  [GlobalSearchRecordTypeEnum.Fund]: 'Fund',
  [GlobalSearchRecordTypeEnum.Grant]: 'Grant',
  [GlobalSearchRecordTypeEnum.Hifo]: 'HIFO',
  [GlobalSearchRecordTypeEnum.HifoCascade]: 'HIFO Cascade',
  [GlobalSearchRecordTypeEnum.MatchCampaign]: 'Match Campaign',
  [GlobalSearchRecordTypeEnum.MatchCampaignCategory]: 'Match Campaign Category',
  [GlobalSearchRecordTypeEnum.NewsItem]: 'News',
  [GlobalSearchRecordTypeEnum.Policy]: 'Policy',
  [GlobalSearchRecordTypeEnum.ProvisionalPledge]: 'Pledge',
  [GlobalSearchRecordTypeEnum.Role]: 'Role',
  [GlobalSearchRecordTypeEnum.TargetAudience]: 'Target Audience',
  [GlobalSearchRecordTypeEnum.TrainingModule]: 'Training Module',
  [GlobalSearchRecordTypeEnum.User]: 'User',
}

export function globalSearchHitHref(hit: GlobalSearchHit): string {
  return GLOBAL_SEARCH_HREFS[hit.recordType]?.(hit.recordId) ?? '/'
}

export function globalSearchRecordTypeLabel(
  recordType: GlobalSearchRecordTypeEnum
): string {
  return GLOBAL_SEARCH_RECORD_TYPE_LABELS[recordType] ?? 'Record'
}

const GLOBAL_SEARCH_RECORD_TYPE_ICONS: Partial<
  Record<
    GlobalSearchRecordTypeEnum,
    {
      component: Component
      colorClass: string
    }
  >
> = {
  [GlobalSearchRecordTypeEnum.Grant]: {
    component: MoneySharingGiveIcon,
    colorClass: 'text-fp-green-7',
  },
  [GlobalSearchRecordTypeEnum.Charity]: {
    component: ShopIcon,
    colorClass: 'text-fp-red-4',
  },
  [GlobalSearchRecordTypeEnum.User]: {
    component: UserSideIcon,
    colorClass: 'text-fp-purple-7',
  },
  [GlobalSearchRecordTypeEnum.ProvisionalPledge]: {
    component: InvestmentAgreementIcon,
    colorClass: 'text-fp-gray-4',
  },
}

export const GLOBAL_SEARCH_TOOLS_ICON = {
  component: ApplicationWindowIcon,
  colorClass: 'text-fp-gray-4',
}

export function globalSearchRecordTypeIcon(
  recordType: GlobalSearchRecordTypeEnum
) {
  return GLOBAL_SEARCH_RECORD_TYPE_ICONS[recordType]
}
